import React, { useState, useEffect } from 'react';
import { AlertCircle, Plus, Edit3, Trash2, Save, X, Activity, HeartPulse } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { adminAPI } from '../../lib/api';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';

const categoryOptions = [
  { value: 'general', label: 'General' },
  { value: 'digestive', label: 'Digestive' },
  { value: 'hormonal', label: 'Hormonal' },
  { value: 'autoimmune', label: 'Autoimmune' },
  { value: 'cardiovascular', label: 'Cardiovascular' },
  { value: 'metabolic', label: 'Metabolic' },
  { value: 'neurological', label: 'Neurological' },
  { value: 'cancer', label: 'Cancer/Oncology' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'musculoskeletal', label: 'Musculoskeletal' },
  { value: 'mental_health', label: 'Mental Health' },
  { value: 'other', label: 'Other' },
];

export default function AdminHealthIssuesPage() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingIssue, setEditingIssue] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    label: '',
    description: '',
    category: 'general',
    active: true,
  });

  useEffect(() => {
    loadIssues();
  }, []);

  const loadIssues = async () => {
    try {
      const response = await adminAPI.getHealthIssues();
      setIssues(response.data.issues || []);
    } catch (error) {
      console.error('Failed to load health issues:', error);
      toast.error('Failed to load health issues');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      id: '',
      label: '',
      description: '',
      category: 'general',
      active: true,
    });
  };

  const handleAddIssue = async (e) => {
    e.preventDefault();
    if (!formData.id || !formData.label) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.createHealthIssue(formData);
      toast.success('Health issue created');
      setShowAddDialog(false);
      resetForm();
      loadIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create health issue');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateIssue = async (e) => {
    e.preventDefault();
    if (!formData.label) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.updateHealthIssue(editingIssue.id, {
        label: formData.label,
        description: formData.description,
        category: formData.category,
        active: formData.active,
      });
      toast.success('Health issue updated');
      setEditingIssue(null);
      resetForm();
      loadIssues();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update health issue');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteIssue = async (issueId) => {
    try {
      await adminAPI.deleteHealthIssue(issueId);
      toast.success('Health issue deleted');
      loadIssues();
    } catch (error) {
      toast.error('Failed to delete health issue');
    }
  };

  const startEdit = (issue) => {
    setFormData({
      id: issue.id,
      label: issue.label,
      description: issue.description || '',
      category: issue.category || 'general',
      active: issue.active !== false,
    });
    setEditingIssue(issue);
  };

  const cancelEdit = () => {
    setEditingIssue(null);
    resetForm();
  };

  const generateIdFromLabel = (label) => {
    return label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  };

  // Group issues by category
  const groupedIssues = issues.reduce((acc, issue) => {
    const category = issue.category || 'general';
    if (!acc[category]) acc[category] = [];
    acc[category].push(issue);
    return acc;
  }, {});

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl text-[#4A2C2A]">Health Issues</h1>
            <p className="text-[#4A2C2A]/60 mt-1">
              Manage health conditions shown during onboarding. Issues with higher priority in RAG override general advice.
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <Button 
              onClick={() => { resetForm(); setShowAddDialog(true); }}
              className="bg-[#E8A095] text-[#4A2C2A] hover:bg-[#D68C82]"
              data-testid="add-health-issue-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Issue
            </Button>
            <DialogContent className="bg-white max-w-md">
              <DialogHeader>
                <DialogTitle className="text-[#4A2C2A]">Add Health Issue</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddIssue} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-[#4A2C2A]">Label *</Label>
                  <Input
                    value={formData.label}
                    onChange={(e) => {
                      const label = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        label,
                        id: prev.id || generateIdFromLabel(label)
                      }));
                    }}
                    placeholder="e.g., Type 2 Diabetes"
                    className="input-warm"
                    data-testid="health-issue-label-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#4A2C2A]">ID *</Label>
                  <Input
                    value={formData.id}
                    onChange={(e) => setFormData(prev => ({ ...prev, id: e.target.value }))}
                    placeholder="e.g., type_2_diabetes"
                    className="input-warm"
                    data-testid="health-issue-id-input"
                  />
                  <p className="text-xs text-[#4A2C2A]/50">Used internally for matching. Auto-generated from label.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#4A2C2A]">Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger className="input-warm" data-testid="health-issue-category-select">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categoryOptions.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#4A2C2A]">Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of this condition"
                    className="input-warm"
                    rows={2}
                    data-testid="health-issue-description-input"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.active}
                    onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    className="rounded border-[#E8A095]"
                  />
                  <Label htmlFor="active" className="text-[#4A2C2A] cursor-pointer">Active (shown during onboarding)</Label>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving}
                    className="bg-[#4A2C2A] text-white hover:bg-[#382120]"
                    data-testid="save-health-issue-btn"
                  >
                    {saving ? 'Saving...' : 'Create Issue'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Banner */}
        <div className="bg-[#8DA399]/10 border border-[#8DA399]/20 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <HeartPulse className="w-5 h-5 text-[#8DA399] mt-0.5" />
            <div>
              <p className="text-sm text-[#4A2C2A] font-medium">Priority System</p>
              <p className="text-sm text-[#4A2C2A]/70 mt-1">
                Health issues have the <strong>highest priority</strong> in the RAG system. 
                When a user has a health condition, advice from condition-specific document folders will 
                <strong> override</strong> conflicting advice from blood type, genotype, or general folders.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : issues.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-[#E8A095]/20">
            <AlertCircle className="w-12 h-12 mx-auto text-[#4A2C2A]/30 mb-4" />
            <h3 className="font-medium text-[#4A2C2A]">No Health Issues</h3>
            <p className="text-[#4A2C2A]/60 mt-1">Add health conditions that users can select during onboarding.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {categoryOptions.map(cat => {
              const categoryIssues = groupedIssues[cat.value];
              if (!categoryIssues || categoryIssues.length === 0) return null;
              
              return (
                <div key={cat.value} className="bg-white rounded-2xl border border-[#E8A095]/20 overflow-hidden">
                  <div className="px-6 py-3 bg-[#FDFBF7] border-b border-[#E8A095]/10">
                    <h2 className="font-medium text-[#4A2C2A]">{cat.label}</h2>
                  </div>
                  <div className="divide-y divide-[#E8A095]/10">
                    {categoryIssues.map((issue) => (
                      <div key={issue.id} className="p-4">
                        {editingIssue?.id === issue.id ? (
                          <form onSubmit={handleUpdateIssue} className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-[#4A2C2A]">Label</Label>
                                <Input
                                  value={formData.label}
                                  onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                                  className="input-warm"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-[#4A2C2A]">Category</Label>
                                <Select
                                  value={formData.category}
                                  onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
                                >
                                  <SelectTrigger className="input-warm">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {categoryOptions.map(c => (
                                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                            <div className="space-y-2">
                              <Label className="text-[#4A2C2A]">Description</Label>
                              <Textarea
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                className="input-warm"
                                rows={2}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id={`active-${issue.id}`}
                                checked={formData.active}
                                onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                                className="rounded border-[#E8A095]"
                              />
                              <Label htmlFor={`active-${issue.id}`} className="text-[#4A2C2A] cursor-pointer">Active</Label>
                            </div>
                            <div className="flex justify-end gap-2">
                              <Button type="button" variant="ghost" size="sm" onClick={cancelEdit}>
                                <X className="w-4 h-4 mr-1" /> Cancel
                              </Button>
                              <Button 
                                type="submit" 
                                size="sm" 
                                disabled={saving}
                                className="bg-[#8DA399] text-white hover:bg-[#7A9186]"
                              >
                                <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
                              </Button>
                            </div>
                          </form>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${issue.active !== false ? 'bg-[#E8A095]/20' : 'bg-gray-100'}`}>
                                <Activity className={`w-5 h-5 ${issue.active !== false ? 'text-[#E8A095]' : 'text-gray-400'}`} />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-[#4A2C2A]">{issue.label}</span>
                                  {issue.active === false && (
                                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded">Inactive</span>
                                  )}
                                </div>
                                {issue.description && (
                                  <p className="text-sm text-[#4A2C2A]/60 mt-0.5">{issue.description}</p>
                                )}
                                <p className="text-xs text-[#4A2C2A]/40 mt-0.5">ID: {issue.id}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => startEdit(issue)}
                                className="text-[#4A2C2A]/60 hover:text-[#4A2C2A]"
                                data-testid={`edit-issue-${issue.id}`}
                              >
                                <Edit3 className="w-4 h-4" />
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500/60 hover:text-red-500"
                                    data-testid={`delete-issue-${issue.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-white">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>Delete Health Issue</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      Are you sure you want to delete "{issue.label}"? This will also remove it from any users who have selected it.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteIssue(issue.id)}
                                      className="bg-red-500 hover:bg-red-600"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
